import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppConfig } from '@app/core/app.config';
import { Check } from '@app/core/model/entities/asset/check';
import { CheckType } from '@app/core/model/entities/asset/check-type';
import { CheckTypeService } from '@app/shared/services/check-type.service';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export interface CheckResult {
  result?: string;
  climateLabel?: string;
  powerLabel?: string;
  observation?: string;
}

@Component({
  selector: 'check-result-cell',
  templateUrl: './check-result-cell.component.html',
  styleUrls: ['./check-result-cell.component.scss'],
  standalone: false
})
export class CheckResultCellComponent implements ICellRendererAngularComp, OnInit {

  protected params: ICellRendererParams;
  protected checkTypeCode: string;
  protected displayValue: string;
  protected climateLabel: string;
  protected powerLabel: string;
  protected checkTypes: { [k: string]: CheckType };

  protected destroyRef = inject(DestroyRef);
  protected readonly emptyValue = inject(AppConfig).EMPTY_FIELD_VALUE;
  protected readonly gridNullFlag = inject(AppConfig).GRID_NULL_FLAG;
  private checkTypeService = inject(CheckTypeService);

  public ngOnInit(): void {
    this.checkTypeService.checkTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(checkTypes => this.checkTypes = checkTypes);
  }

  /**
   * Method used by Ag-grid to initialise the cell with grid parameters
   * There are two cases for the cell : the first one is when we receive an array of assetChecks (master grid) and the second is when we receive a simple check (detail grid),
   * so we need to check if the data received has one or more elements.
   * @param params
   */
  public agInit(params: ICellRendererParams<Check, CheckResult>): void {
    this.params = params;

    const { type, value } = (params.node.group)
      // When the row is a group, only the check result is preserved, all common values are merged together.
      ? { type: null, value: params.value }
      // In ungroup row, fetch the check type from the row data.
      : { type: params.data.type, value: params.value };
    this.checkTypeCode = type;

    if (!this.isGridValueInvalid(value?.toString())) {
      if (value.hasOwnProperty('observation')) {
        this.displayValue = value.observation;
      } else if (value.hasOwnProperty('result')) {
        this.displayValue = value.result;
      } else if (value.hasOwnProperty('climateLabel') && value.hasOwnProperty('powerLabel')) {
        this.climateLabel = value.climateLabel;
        this.powerLabel = value.powerLabel;
      }
    }
  }

  /**
   * Method used by Ag-Grid to tell the grid to refresh this cell
   * This component does not include refresh logic
   * @returns {boolean}
   */
  public refresh(_: ICellRendererParams): boolean {
    return false;
  }

  /**
   * Check if the Value of a cell is invalid
   * @param value the value of the grid cell
   * @return true if invalid, otherwise false
   */
  private isGridValueInvalid(value?: string): boolean {
    return value == null || value === '' || value === this.gridNullFlag;
  }
}
